<script>
import MyTable from '@/components/MyTable.vue'
import { reactive, ref, onMounted, inject } from 'vue'
import { useRouter } from 'vue-router'
import redeMarketingApi from '@/services/api/redeMarketingRepository'

export default {
  components: { MyTable },
  setup() {
    const router = useRouter()
    const swal = inject('$swal')

    const state = reactive({
      membros: [],
      busca: '',
      page: 0,
      size: 20,
      totalPaginas: 0,
      loading: false,
      sincronizando: false,
    })

    const columns = ref([
      { label: 'ID', field: 'id' },
      { label: 'Nome', field: 'nome' },
      { label: 'Email', field: 'email' },
      { label: 'Código', field: 'codigoIndicacao' },
      { label: 'Status', field: 'status' },
      { label: 'Ações', field: 'actions' },
    ])

    let debounceTimer = null

    function debouncedBuscar() {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        state.page = 0
        carregar()
      }, 400)
    }

    async function carregar() {
      state.loading = true
      try {
        const resp = await redeMarketingApi.ListarMembros({
          busca: state.busca,
          page: state.page,
          size: state.size,
        })
        state.membros = resp.data.content
        state.totalPaginas = resp.data.totalPages
      } catch (e) {
        swal({ icon: 'error', title: 'Erro', text: e.response?.data?.message || e.message })
      } finally {
        state.loading = false
      }
    }

    function mudarPagina(novaPage) {
      if (novaPage < 0 || novaPage >= state.totalPaginas) return
      state.page = novaPage
      carregar()
    }

    async function ativar(membro) {
      try {
        await redeMarketingApi.AtivarMembro(membro.id)
        swal({ icon: 'success', title: 'Ativado', text: `Membro ${membro.nome} ativado.` })
        carregar()
      } catch (e) {
        swal({ icon: 'error', title: 'Erro', text: e.response?.data?.message || e.message })
      }
    }

    async function sincronizar() {
      swal({
        title: 'Sincronizar do Laravel?',
        text: 'Trará todos os coaches do Diário de Treino para a rede.',
        showCancelButton: true,
        confirmButtonText: 'Sim, sincronizar',
        cancelButtonText: 'Cancelar',
      }).then(async (result) => {
        if (!result.isConfirmed) return
        state.sincronizando = true
        try {
          const resp = await redeMarketingApi.SincronizarCoaches()
          const c = resp.data.criados || 0
          const a = resp.data.atualizados || 0
          const x = resp.data.cancelados || 0
          swal({
            icon: 'success',
            title: 'Sincronizado!',
            text: `${c} criados · ${a} atualizados · ${x} marcados como CANCELADO (sumiram do Laravel).`,
          })
          carregar()
        } catch (e) {
          swal({ icon: 'error', title: 'Erro', text: e.response?.data?.message || e.message })
        } finally {
          state.sincronizando = false
        }
      })
    }

    function novoMembro() {
      router.push('/rede-marketing/membros/novo')
    }

    function badgeStatus(status) {
      switch (status) {
        case 'ATIVO': return 'bg-success'
        case 'PENDENTE': return 'bg-warning text-dark'
        case 'SUSPENSO': return 'bg-danger'
        case 'CANCELADO': return 'bg-secondary'
        default: return 'bg-secondary'
      }
    }

    onMounted(carregar)

    return {
      state,
      columns,
      debouncedBuscar,
      mudarPagina,
      ativar,
      sincronizar,
      novoMembro,
      badgeStatus,
    }
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <div class="d-flex align-items-center gap-2">
        <router-link to="/rede-marketing" class="btn btn-sm btn-light">
          <i class="material-icons align-middle">arrow_back</i>
        </router-link>
        <h3 class="mb-0">Treinadores na Rede</h3>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-sm btn-outline-primary d-flex align-items-center"
                @click="sincronizar" :disabled="state.sincronizando">
          <i class="material-icons me-1" style="font-size: 18px;">sync</i>
          {{ state.sincronizando ? 'Sincronizando...' : 'Sincronizar do Laravel' }}
        </button>
        <button class="btn btn-sm btn-success d-flex align-items-center" @click="novoMembro">
          <i class="material-icons me-1" style="font-size: 18px;">person_add</i>
          Novo Treinador
        </button>
      </div>
    </div>

    <div class="mb-3">
      <input v-model="state.busca" @input="debouncedBuscar"
             placeholder="Buscar por nome ou email..."
             class="form-control form-control-sm" />
    </div>

    <div v-if="state.loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="text-muted small mt-2">Carregando...</p>
    </div>

    <MyTable v-else :data="state.membros" :columns="columns" :hideHeader="false" :itemsPerPage="20">
      <template v-slot:row="{ row }">
        <td class="text-sm text-muted fw-bold">#{{ row.id }}</td>
        <td class="text-sm">
          {{ row.nome }}
          <span v-if="row.origemCadastro === 'LINK_PUBLICO'"
                class="badge bg-info text-xs ms-1"
                title="Cadastrado via link de indicação">
            <i class="material-icons align-middle" style="font-size: 12px;">link</i>
            via link
          </span>
          <span v-if="!row.laravelUserId"
                class="badge bg-warning text-dark text-xs ms-1"
                title="Usuário ainda não completou cadastro no Laravel">
            <i class="material-icons align-middle" style="font-size: 12px;">pending</i>
            pendente Laravel
          </span>
        </td>
        <td class="text-sm">{{ row.email }}</td>
        <td class="text-sm">
          <code class="text-dark">{{ row.codigoIndicacao }}</code>
        </td>
        <td class="text-sm">
          <span :class="['badge', badgeStatus(row.status)]">{{ row.status }}</span>
        </td>
        <td class="text-sm">
          <div class="float-end d-flex gap-1 flex-wrap">
            <router-link
              :to="`/rede-marketing/genealogia/${row.id}`"
              class="btn btn-sm btn-outline-info d-flex align-items-center"
              title="Ver árvore"
            >
              <i class="material-icons" style="font-size: 16px;">account_tree</i>
            </router-link>
            <router-link
              :to="`/rede-marketing/rede-linear/${row.id}`"
              class="btn btn-sm btn-outline-primary d-flex align-items-center"
              title="Ver rede linear"
            >
              <i class="material-icons" style="font-size: 16px;">list_alt</i>
            </router-link>
            <button
              v-if="row.status === 'PENDENTE'"
              class="btn btn-sm btn-outline-success d-flex align-items-center"
              title="Ativar"
              @click="ativar(row)"
            >
              <i class="material-icons" style="font-size: 16px;">check_circle</i>
            </button>
          </div>
        </td>
      </template>
    </MyTable>

    <div v-if="state.totalPaginas > 1" class="d-flex justify-content-center align-items-center gap-3 mt-3">
      <button class="btn btn-sm btn-outline-secondary" @click="mudarPagina(state.page - 1)" :disabled="state.page === 0">
        <i class="material-icons" style="font-size: 16px;">chevron_left</i>
      </button>
      <span class="small text-muted">Página {{ state.page + 1 }} de {{ state.totalPaginas }}</span>
      <button class="btn btn-sm btn-outline-secondary" @click="mudarPagina(state.page + 1)" :disabled="state.page + 1 >= state.totalPaginas">
        <i class="material-icons" style="font-size: 16px;">chevron_right</i>
      </button>
    </div>
  </div>
</template>
